import { createReducer } from '@common/redux-utils';
import {
  SaveRealEstateOfficeStartAction,
  SaveRealEstateOfficeSuccessAction,
  SaveRealEstateOfficeFailureAction,
  GetRealEstateOfficeStartAction,
  GetRealEstateOfficeSuccessAction,
  GetRealEstateOfficeFailureAction,
  GetRealEstateAgentsByOfficeStartAction,
  GetRealEstateAgentsByOfficeSuccessAction,
  GetRealEstateAgentsByOfficeFailureAction,
  RealEstateOfficeActionType,
  RealEstateOfficeSearchInitAction,
  RealEstateOfficeSearchSuccessAction,
  RealEstateOfficeSearchFailureAction
} from './realestate-office.actions';
import { RealEstateOfficeViewModel, RealEstateOfficeSearchViewModel } from '@models/RealEstateOffice';
import { AgentSearchViewModel } from '@models/RealEstateAgent';

// Defining the state of the page.
// Should be one per page.
// All properties should be readonly.
export interface RealEstateOfficeState {
  readonly message?: string;
  readonly saving?: boolean;
  readonly realEstateOfficeVM: RealEstateOfficeViewModel | null;
  readonly id: number;
  readonly query: string | null;
  readonly realEstateOfficeSearchLoading: boolean;
  readonly realEstateOfficeSearchPageCount: number;
  readonly realEstateOfficeSearchPageNumber: number;
  readonly realEstateOfficeSearchPageSize: number;
  readonly realEstateOfficeSearchOrderBy: string;
  readonly realEstateOffices: RealEstateOfficeSearchViewModel[];
  readonly realEstateAgents: Array<AgentSearchViewModel>;
}

// This is the initial state of the page
const initialState: RealEstateOfficeState = {
  message: 'Initialized',
  realEstateOfficeVM: null,
  id: 0,
  query: null,
  realEstateOfficeSearchLoading: false,
  realEstateOfficeSearchPageCount: 0,
  realEstateOfficeSearchPageNumber: 1,
  realEstateOffices: [],
  realEstateOfficeSearchPageSize: 10,
  realEstateOfficeSearchOrderBy: 'id',
  realEstateAgents: []
};

/********** Save **********/
function handleSaveRealEstateOfficeStart(
  state: RealEstateOfficeState,
  action: SaveRealEstateOfficeStartAction
): RealEstateOfficeState {
  return {
    ...state,
    saving: true
  };
}

function handleSaveRealEstateOfficeSuccess(
  state: RealEstateOfficeState,
  action: SaveRealEstateOfficeSuccessAction
): RealEstateOfficeState {
  if (!action.currentId) {
    return {
      ...state,
      saving: false
    };
  }
  return {
    ...state,
    saving: false,
    message: 'SUCCESS - real estate office saved - ' + action.currentId
  };
}

function handleSaveRealEstateOfficeFailure(
  state: RealEstateOfficeState,
  action: SaveRealEstateOfficeFailureAction
): RealEstateOfficeState {
  if (!action.errorMessage) {
    return {
      ...state,
      saving: false
    };
  }
  return {
    ...state,
    saving: false,
    message: 'ERROR - save real estate office failed - ' + action.errorMessage
  };
}

/********** Get **********/
function handleGetRealEstateOfficeStart(
  state: RealEstateOfficeState,
  action: GetRealEstateOfficeStartAction
): RealEstateOfficeState {
  return state;
}

function handleGetRealEstateOfficeSuccess(
  state: RealEstateOfficeState,
  action: GetRealEstateOfficeSuccessAction
): RealEstateOfficeState {
  if (!action.currentId) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - get real estate office done - ' + action.currentId,
    realEstateOfficeVM: action.realEstateOfficeVM,
    id: action.currentId
  };
}

function handleGetRealEstateOfficeFailure(
  state: RealEstateOfficeState,
  action: GetRealEstateOfficeFailureAction
): RealEstateOfficeState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - get real estate office failed - ' + action.errorMessage
  };
}

function handleGetRealEstateAgentsByOfficeStart(
  state: RealEstateOfficeState,
  action: GetRealEstateAgentsByOfficeStartAction
): RealEstateOfficeState {
  return state;
}

function handleGetRealEstateAgentsByOfficeSuccess(
  state: RealEstateOfficeState,
  action: GetRealEstateAgentsByOfficeSuccessAction
): RealEstateOfficeState {
  if (!action.realEstateAgents) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - get real estate agents by office done - ',
    realEstateAgents: action.realEstateAgents
  };
}

function handleGetRealEstateAgentsByOfficeFailure(
  state: RealEstateOfficeState,
  action: GetRealEstateAgentsByOfficeFailureAction
): RealEstateOfficeState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - get real estate office failed - ' + action.errorMessage
  };
}

/********** Search **********/
function handleRealEstateOfficesSearchInit(
  state: RealEstateOfficeState,
  action: RealEstateOfficeSearchInitAction
): RealEstateOfficeState {
  return {
    ...state,
    message: 'Search Init!',
    query: action.query,
    realEstateOfficeSearchLoading: true
  };
}
function handleRealEstateOfficesSearchFailure(
  state: RealEstateOfficeState,
  action: RealEstateOfficeSearchFailureAction
): RealEstateOfficeState {
  return {
    ...state,
    message: action.errorMessage,
    realEstateOfficeSearchLoading: false
  };
}
function handleRealEstateOfficesSearchSuccess(
  state: RealEstateOfficeState,
  action: RealEstateOfficeSearchSuccessAction
): RealEstateOfficeState {
  return {
    ...state,
    message: 'Search Success!',
    realEstateOfficeSearchLoading: false,
    realEstateOfficeSearchPageCount: Math.ceil(action.result.totalResults / action.result.pageSize),
    realEstateOfficeSearchPageNumber: action.pageNumber,
    realEstateOfficeSearchPageSize: action.pageSize,
    realEstateOffices: action.result.results
  };
}

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const RealEstateOfficeReducer = createReducer(initialState, {
  /********** Save **********/
  [RealEstateOfficeActionType.SaveRealEstateOfficeStart]: handleSaveRealEstateOfficeStart,
  [RealEstateOfficeActionType.SaveRealEstateOfficeSuccess]: handleSaveRealEstateOfficeSuccess,
  [RealEstateOfficeActionType.SaveRealEstateOfficeFailure]: handleSaveRealEstateOfficeFailure,

  /********** Get **********/
  [RealEstateOfficeActionType.GetRealEstateOfficeStart]: handleGetRealEstateOfficeStart,
  [RealEstateOfficeActionType.GetRealEstateOfficeSuccess]: handleGetRealEstateOfficeSuccess,
  [RealEstateOfficeActionType.GetRealEstateOfficeFailure]: handleGetRealEstateOfficeFailure,
  [RealEstateOfficeActionType.GetRealEstateAgentsByOfficeStart]: handleGetRealEstateAgentsByOfficeStart,
  [RealEstateOfficeActionType.GetRealEstateAgentsByOfficeSuccess]: handleGetRealEstateAgentsByOfficeSuccess,
  [RealEstateOfficeActionType.GetRealEstateAgentsByOfficeFailure]: handleGetRealEstateAgentsByOfficeFailure,

  /********* Search ******/
  [RealEstateOfficeActionType.RealEstateOfficeSearchInit]: handleRealEstateOfficesSearchInit,
  [RealEstateOfficeActionType.RealEstateOfficeSearchFailure]: handleRealEstateOfficesSearchFailure,
  [RealEstateOfficeActionType.RealEstateOfficeSearchSuccess]: handleRealEstateOfficesSearchSuccess
});



// WEBPACK FOOTER //
// ./src/realestate-office/realestate-office.reducer.ts